import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Plus, X, Upload, Download, Crown, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLES = [
  { value: "general", label: "General" },
  { value: "lieutenant", label: "Lieutenant" },
  { value: "captain", label: "Captain" },
];
const DIVISIONS = ["Mariners", "Skippers", "Navigators"];

const selectCls = "h-9 px-2 text-xs border rounded-md bg-white font-medium cursor-pointer";

export default function ColorWarLeadershipPanel({ token }) {
  const headers = { Authorization: `Bearer ${token || localStorage.getItem("token")}` };
  const [leadership, setLeadership] = useState([]);
  const [specialty, setSpecialty] = useState([]);
  const [form, setForm] = useState({ role: "general", name: "", division: "Mariners", color: "blue" });
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/color-war/leadership`, { headers }),
        axios.get(`${API}/color-war/specialty-cits`, { headers }),
      ]);
      setLeadership(l.data.leadership || []);
      setSpecialty(s.data.specialty_cits || []);
    } catch { toast.error("Failed to load leadership data"); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const addLeader = async () => {
    if (!form.name.trim()) { toast.error("Enter a name"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/color-war/leadership`, form, { headers });
      toast.success(`${form.role.charAt(0).toUpperCase() + form.role.slice(1)} added`);
      setForm({ ...form, name: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
    setAdding(false);
  };

  const removeLeader = async (id) => {
    try {
      await axios.delete(`${API}/color-war/leadership/${id}`, { headers });
      load();
    } catch { toast.error("Failed to remove"); }
  };

  const uploadSpecialty = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/color-war/specialty-cit/upload`, fd, { headers });
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const clearSpecialty = async () => {
    if (!window.confirm("Remove all specialty CITs?")) return;
    try {
      await axios.delete(`${API}/color-war/specialty-cits`, { headers });
      load();
    } catch { toast.error("Failed to clear"); }
  };

  const downloadTemplate = async (kind) => {
    try {
      const res = await axios.get(`${API}/color-war/templates/${kind}`, { headers, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = kind === "staff" ? "Color_War_Staff_Template.csv" : "Specialty_CIT_Template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Template download failed"); }
  };

  const colorBadge = (c) => (
    <Badge className={`text-[10px] ${c === "blue" ? "bg-blue-600" : "bg-gray-400 text-gray-900"}`}>{c}</Badge>
  );

  const rolesWithEntries = ROLES.map((r) => ({ ...r, entries: leadership.filter((l) => l.role === r.value) }));

  return (
    <Card data-testid="cw-leadership-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-bold">Leadership &amp; Specialty CITs</h3>
          <span className="text-[10px] text-muted-foreground">shown on the team PDF</span>
        </div>

        {/* Add leader form */}
        <div className="flex flex-wrap items-center gap-2">
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={selectCls} data-testid="cw-leader-role">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") addLeader(); }}
            placeholder="Full name"
            className="h-9 text-xs w-44"
            data-testid="cw-leader-name"
          />
          <select value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} className={selectCls} data-testid="cw-leader-division">
            {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className={selectCls} data-testid="cw-leader-color">
            <option value="blue">Blue</option>
            <option value="white">White</option>
          </select>
          <Button size="sm" className="h-9 text-xs bg-amber-600 hover:bg-amber-700" onClick={addLeader} disabled={adding} data-testid="cw-leader-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>

        {/* Leadership lists */}
        {leadership.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-3">
            {rolesWithEntries.map((r) => (
              <div key={r.value} data-testid={`cw-leaders-${r.value}`}>
                <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mb-1">{r.label}s ({r.entries.length})</p>
                <div className="space-y-1">
                  {r.entries.length === 0 && <p className="text-[11px] text-muted-foreground italic">None yet</p>}
                  {r.entries.map((l) => (
                    <div key={l.id} className="flex items-center gap-1.5 py-1 px-2 rounded bg-muted/40 border" data-testid={`cw-leader-row-${l.id}`}>
                      <span className="text-xs font-semibold flex-1 min-w-0 truncate">{l.name}</span>
                      <span className="text-[10px] text-muted-foreground">{l.division}</span>
                      {colorBadge(l.color)}
                      <button onClick={() => removeLeader(l.id)} className="p-0.5 rounded hover:bg-red-100" data-testid={`cw-leader-delete-${l.id}`}>
                        <X className="w-3 h-3 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Specialty CITs */}
        <div className="pt-3 border-t space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Specialty CITs ({specialty.length})</p>
            <label className="inline-flex items-center h-8 px-3 text-xs border rounded-md cursor-pointer hover:bg-muted" data-testid="cw-specialty-upload">
              <Upload className="w-3 h-3 mr-1" /> {uploading ? "Uploading…" : "Upload Excel"}
              <input type="file" accept=".xlsx,.csv" className="hidden" onChange={uploadSpecialty} disabled={uploading} />
            </label>
            {specialty.length > 0 && (
              <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200" onClick={clearSpecialty} data-testid="cw-specialty-clear">
                <Trash2 className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
            <span className="text-[10px] text-muted-foreground">Columns: First Name, Last Name, Color (blue/white)</span>
          </div>
          {specialty.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="cw-specialty-list">
              {specialty.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border bg-muted/30">
                  {s.name} {colorBadge(s.color_war)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Templates */}
        <div className="pt-3 border-t flex flex-wrap items-center gap-2">
          <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Upload Templates</p>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => downloadTemplate("staff")} data-testid="cw-template-staff">
            <Download className="w-3 h-3 mr-1" /> Staff CSV Template
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => downloadTemplate("specialty-cit")} data-testid="cw-template-specialty">
            <Download className="w-3 h-3 mr-1" /> Specialty CIT Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
